import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import {
    ComposedChart,
    Area,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Bar,
    Legend,
    ReferenceLine,
} from 'recharts';
import { Loader2, AlertCircle, BarChart3, RefreshCw } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import { workspaceAPI } from '../lib/api';

const AllTimeTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0]?.payload;
    if (!data) return null;
    return (
        <div className="bg-white border border-brand-border rounded-lg shadow-lg p-3 text-sm">
            <p className="font-semibold text-brand-text-primary mb-1">
                {format(parseISO(data.date), 'd MMMM yyyy', { locale: idLocale })}
            </p>
            <div className="space-y-1">
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#2F6F63' }} />
                    <span className="text-brand-text-muted">Kumulatif:</span>
                    <span className="font-semibold text-brand-text-primary">{data.cumulative}</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#6EC1E4' }} />
                    <span className="text-brand-text-muted">Harian:</span>
                    <span className="font-semibold text-brand-text-primary">{data.daily}</span>
                </div>
                {data.target_cumulative !== undefined && (
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#F97316' }} />
                        <span className="text-brand-text-muted">Target Kumulatif:</span>
                        <span className="font-semibold text-brand-text-primary">{data.target_cumulative}</span>
                    </div>
                )}
                {data.target !== undefined && (
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#F59E0B' }} />
                        <span className="text-brand-text-muted">Target Harian:</span>
                        <span className="font-semibold text-brand-text-primary">{data.target}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default function SCurveAllTimeChart({ workspaceId, spaceId }) {
    const [data, setData] = useState([]);
    const [meta, setMeta] = useState({ start_date: null, end_date: null, total_done_current: null });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        if (!workspaceId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await workspaceAPI.getStatTimelineAllTime(workspaceId, spaceId || null);
            setData(res.data?.data || []);
            setMeta({
                start_date: res.data?.start_date,
                end_date: res.data?.end_date,
                total_done_current: res.data?.total_done_current ?? null,
            });
        } catch (err) {
            console.error('Failed to load all-time S-curve:', err);
            setError('Gagal memuat data kurva S all time');
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [workspaceId, spaceId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const todayStr = format(new Date(), 'yyyy-MM-dd');
    const todayInRange = meta.start_date && meta.end_date && todayStr >= meta.start_date && todayStr <= meta.end_date;

    const totalDays = data.length;
    const maxCumulative = data.length > 0 ? Math.max(...data.map(d => d.cumulative)) : 0;
    const maxDaily = data.length > 0 ? Math.max(...data.map(d => d.daily)) : 0;
    const totalDone = meta.total_done_current !== null ? meta.total_done_current : (data.length > 0 ? (data[data.length - 1]?.cumulative || 0) : 0);
    const totalTarget = data.length > 0 ? (data[data.length - 1]?.target_cumulative || 0) : 0;

    // X-axis tick formatter: short date if ≤60 days, month+year if longer
    const xTickFormatter = (value) => {
        try {
            if (totalDays <= 60) return format(parseISO(value), 'd MMM', { locale: idLocale });
            return format(parseISO(value), "MMM ''yy", { locale: idLocale });
        } catch {
            return value;
        }
    };

    // Show fewer ticks for long ranges
    const xInterval = totalDays <= 30 ? 1 : totalDays <= 90 ? 6 : totalDays <= 365 ? 29 : 89;

    const formatDateDisplay = (d) => {
        if (!d) return '-';
        try { return format(parseISO(d), 'd MMM yyyy', { locale: idLocale }); }
        catch { return d; }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full min-h-[300px]">
                <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-full min-h-[300px] text-brand-text-muted">
                <AlertCircle className="w-8 h-8 mb-2 text-brand-error" />
                <p className="text-sm">{error}</p>
                <Button variant="outline" size="sm" className="mt-2 gap-1" onClick={fetchData}>
                    <RefreshCw className="w-3 h-3" /> Coba Lagi
                </Button>
            </div>
        );
    }

    if (data.length === 0 || maxCumulative === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full min-h-[300px] text-brand-text-muted">
                <BarChart3 className="w-10 h-10 mb-3 opacity-40" />
                <p className="text-sm">Tidak ada data all-time</p>
                <p className="text-xs mt-1">Pastikan task memiliki start date dan due date</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full gap-3 pt-1">
            {/* Period + Summary */}
            <div className="flex items-center justify-between flex-wrap gap-3 px-1">
                <p className="text-sm text-brand-text-muted">
                    Periode: <span className="font-medium text-brand-text-primary">
                        {formatDateDisplay(meta.start_date)} – {formatDateDisplay(meta.end_date)}
                    </span>
                    <span className="ml-2 text-xs">({totalDays} hari)</span>
                </p>
                <div className="flex items-center gap-4 text-sm flex-wrap">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-brand-primary" />
                        <span className="text-brand-text-muted">Total Selesai:</span>
                        <span className="font-semibold text-brand-text-primary">{totalDone} task</span>
                    </div>
                    {totalTarget > 0 && (
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-amber-500" />
                            <span className="text-brand-text-muted">Target:</span>
                            <span className="font-semibold text-brand-text-primary">{totalTarget} task</span>
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-400" />
                        <span className="text-brand-text-muted">Max Harian:</span>
                        <span className="font-semibold text-brand-text-primary">{maxDaily} task</span>
                    </div>
                </div>
            </div>

            {/* Chart — fills remaining space */}
            <div className="flex-1 min-h-0">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <defs>
                            <linearGradient id="colorCumulativeAllTime" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#2F6F63" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#2F6F63" stopOpacity={0.05} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis
                            dataKey="date"
                            tickFormatter={xTickFormatter}
                            interval={xInterval}
                            tick={{ fontSize: 11, fill: '#6B7280' }}
                            stroke="#9CA3AF"
                        />
                        <YAxis
                            yAxisId="left"
                            tick={{ fontSize: 12, fill: '#6B7280' }}
                            stroke="#9CA3AF"
                            label={{ value: 'Kumulatif', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: '#6B7280' } }}
                        />
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            tick={{ fontSize: 12, fill: '#6B7280' }}
                            stroke="#9CA3AF"
                            label={{ value: 'Harian / Target', angle: 90, position: 'insideRight', style: { fontSize: 12, fill: '#6B7280' } }}
                        />
                        <Tooltip content={<AllTimeTooltip />} />
                        <Legend wrapperStyle={{ fontSize: '13px', paddingTop: '10px' }} iconType="circle" />
                        {todayInRange && (
                            <ReferenceLine
                                x={todayStr}
                                yAxisId="left"
                                stroke="#EF4444"
                                strokeWidth={2}
                                strokeDasharray="4 4"
                                label={{ value: 'Hari Ini', position: 'top', fill: '#EF4444', fontSize: 11, fontWeight: 600 }}
                            />
                        )}
                        <Area
                            yAxisId="left"
                            type="monotone"
                            dataKey="cumulative"
                            stroke="#2F6F63"
                            strokeWidth={2.5}
                            fill="url(#colorCumulativeAllTime)"
                            name="Kumulatif"
                        />
                        <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="target_cumulative"
                            stroke="#F97316"
                            strokeWidth={2.5}
                            strokeDasharray="5 5"
                            dot={false}
                            name="Target Kumulatif"
                        />
                        <Bar
                            yAxisId="right"
                            dataKey="daily"
                            fill="#6EC1E4"
                            radius={[4, 4, 0, 0]}
                            barSize={totalDays > 90 ? 4 : 12}
                            name="Harian"
                        />
                        <Bar
                            yAxisId="right"
                            dataKey="target"
                            fill="#F59E0B"
                            radius={[4, 4, 0, 0]}
                            barSize={totalDays > 90 ? 4 : 12}
                            name="Target Harian"
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
